// services/omoreAlertingService.ts
// Real-time alerting system for critical moderation flags

import {
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot,
    getDocs,
    setDoc,
    doc,
    Timestamp,
    Unsubscribe,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { FlagCategory, FlaggedMessage } from '../types/OmoreInteractionTypes';
import { FlagSeverityMap } from '../types/OmoreInteractionTypes';

// ─── Collection Path ────────────────────────────────────────

const ALERTS_PATH = 'omoreInteractions/moderation/alerts';
const MODERATION_FLAGGED_PATH = 'omoreInteractions/moderation/flagged';

// ─── Types ──────────────────────────────────────────────────

export enum AlertSeverity {
    CRITICAL = 'critical',
    HIGH = 'high',
    MEDIUM = 'medium',
    LOW = 'low',
}

export enum AlertChannel {
    EMAIL = 'email',
    IN_APP = 'in_app',
    SLACK = 'slack',
    SMS = 'sms',
}

export interface AlertConfig {
    criticalCategories: FlagCategory[];
    minSeverityThreshold: number;
    channels: AlertChannel[];
    recipients: string[];
    maxAlertsPerHour: number;
    quietHoursStart?: number;
    quietHoursEnd?: number;
}

export interface Alert {
    id: string;
    messageID: string;
    sessionID: string;
    appleID: string;
    explorerName: string;
    content: string;
    flagCategories: FlagCategory[];
    severity: AlertSeverity;
    severityScore: number;
    helperType: string;
    origin: string;
    timestamp: Timestamp;
    sentAt: Timestamp;
    channels: AlertChannel[];
    recipients: string[];
    acknowledged: boolean;
    acknowledgedBy?: string;
    acknowledgedAt?: Timestamp;
}

export interface AlertStats {
    total: number;
    bySeverity: Record<string, number>;
    byCategory: Record<string, number>;
    acknowledged: number;
    avgResponseTimeMinutes: number;
}

// ─── Default Configuration ──────────────────────────────────

const DEFAULT_ALERT_CONFIG: AlertConfig = {
    criticalCategories: ['self_harm', 'violence', 'sexual'],
    minSeverityThreshold: 4,
    channels: [AlertChannel.IN_APP, AlertChannel.EMAIL],
    recipients: [],
    maxAlertsPerHour: 10,
};

// ─── Core Functions ─────────────────────────────────────────

/**
 * Subscribe to critical flags in real-time.
 * Monitors unreviewed flagged messages and triggers alerts for matching criteria.
 */
export function subscribeToCriticalFlags(
    config: Partial<AlertConfig>,
    onAlert: (alert: Alert) => void
): Unsubscribe {
    const fullConfig: AlertConfig = { ...DEFAULT_ALERT_CONFIG, ...config };

    const q = query(
        collection(db, MODERATION_FLAGGED_PATH),
        where('reviewAction', '==', null),
        orderBy('flaggedAt', 'desc'),
        limit(50)
    );

    return onSnapshot(q, async (snapshot) => {
        for (const change of snapshot.docChanges()) {
            if (change.type === 'added') {
                const data = change.doc.data();
                const message: FlaggedMessage = {
                    id: change.doc.id,
                    sessionID: data.sessionID || '',
                    appleID: data.appleID || '',
                    profileID: data.profileID || '',
                    explorerName: data.explorerName || '',
                    helperType: data.helperType || 'general',
                    origin: data.origin || 'home',
                    messageContent: data.messageContent || '',
                    flagCategories: data.flagCategories || [],
                    maxSeverity: data.maxSeverity || 1,
                    flaggedAt: data.flaggedAt,
                    reviewedAt: data.reviewedAt,
                    reviewedBy: data.reviewedBy,
                    reviewAction: data.reviewAction,
                    reviewNotes: data.reviewNotes,
                };

                if (shouldTriggerAlert(message, fullConfig)) {
                    const canSend = await checkRateLimit(fullConfig);
                    if (!canSend || isQuietHours(fullConfig)) continue;

                    try {
                        const alert = await createAlert(message, fullConfig);
                        onAlert(alert);
                    } catch (err) {
                        console.error('Error creating alert:', err);
                    }
                }
            }
        }
    });
}

/**
 * Check if a flagged message should trigger an alert
 */
export function shouldTriggerAlert(message: FlaggedMessage, config: AlertConfig): boolean {
    const hasMatchingCategory = message.flagCategories.some(
        cat => config.criticalCategories.includes(cat)
    );

    const maxSeverity = Math.max(
        ...message.flagCategories.map(cat => FlagSeverityMap[cat] || 1)
    );

    return hasMatchingCategory && maxSeverity >= config.minSeverityThreshold;
}

/**
 * Check if we're under the rate limit for alerts
 */
export async function checkRateLimit(config: AlertConfig): Promise<boolean> {
    try {
        const oneHourAgo = Timestamp.fromDate(new Date(Date.now() - 60 * 60 * 1000));
        const q = query(
            collection(db, ALERTS_PATH),
            where('sentAt', '>=', oneHourAgo),
            orderBy('sentAt', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.size < config.maxAlertsPerHour;
    } catch (error) {
        console.error('Error checking rate limit:', error);
        return true; // Allow on error to avoid missing critical alerts
    }
}

/**
 * Check if current time is within configured quiet hours
 */
export function isQuietHours(config: AlertConfig): boolean {
    if (config.quietHoursStart === undefined || config.quietHoursEnd === undefined) {
        return false;
    }

    const now = new Date();
    const currentHour = now.getUTCHours();

    if (config.quietHoursStart <= config.quietHoursEnd) {
        // Same-day range (e.g., 8-17)
        return currentHour >= config.quietHoursStart && currentHour < config.quietHoursEnd;
    } else {
        // Overnight range (e.g., 22-6)
        return currentHour >= config.quietHoursStart || currentHour < config.quietHoursEnd;
    }
}

/**
 * Create an alert record from a flagged message
 */
export async function createAlert(
    message: FlaggedMessage,
    config: AlertConfig
): Promise<Alert> {
    const maxSeverity = Math.max(
        ...message.flagCategories.map(cat => FlagSeverityMap[cat] || 1)
    );

    let severity: AlertSeverity;
    if (maxSeverity >= 5) severity = AlertSeverity.CRITICAL;
    else if (maxSeverity >= 4) severity = AlertSeverity.HIGH;
    else if (maxSeverity >= 3) severity = AlertSeverity.MEDIUM;
    else severity = AlertSeverity.LOW;

    const alertRef = doc(collection(db, ALERTS_PATH));
    const now = Timestamp.now();

    const alert: Alert = {
        id: alertRef.id,
        messageID: message.id,
        sessionID: message.sessionID,
        appleID: message.appleID,
        explorerName: message.explorerName,
        content: message.messageContent,
        flagCategories: message.flagCategories,
        severity,
        severityScore: maxSeverity,
        helperType: message.helperType,
        origin: message.origin,
        timestamp: message.flaggedAt,
        sentAt: now,
        channels: config.channels,
        recipients: config.recipients,
        acknowledged: false,
    };

    await setDoc(alertRef, alert);
    return alert;
}

/**
 * Dispatch alert to configured channels.
 * Email/Slack/SMS integrations should be configured per deployment.
 */
export async function sendAlert(alert: Alert): Promise<void> {
    // IN_APP is handled by the AlertContext subscription
    // Email, Slack, SMS would require server-side functions or third-party APIs
    // This is a placeholder for future integration
    for (const channel of alert.channels) {
        switch (channel) {
            case AlertChannel.IN_APP:
                // Handled by AlertContext real-time subscription
                break;
            case AlertChannel.EMAIL:
                console.log(`[Alert] Email would be sent to ${alert.recipients.join(', ')} for ${alert.severity} alert`);
                // Integration point: SendGrid/AWS SES
                break;
            case AlertChannel.SLACK:
                console.log(`[Alert] Slack notification for ${alert.severity} alert`);
                // Integration point: Slack webhook
                break;
            case AlertChannel.SMS:
                if (alert.severity === AlertSeverity.CRITICAL) {
                    console.log(`[Alert] SMS would be sent for CRITICAL alert`);
                    // Integration point: Twilio/AWS SNS
                }
                break;
        }
    }
}

// ─── Alert CRUD ─────────────────────────────────────────────

/**
 * Fetch recent alerts, optionally only unacknowledged
 */
export async function fetchRecentAlerts(
    limitCount: number = 50,
    onlyUnacknowledged: boolean = false
): Promise<Alert[]> {
    try {
        const constraints = [];
        if (onlyUnacknowledged) {
            constraints.push(where('acknowledged', '==', false));
        }
        constraints.push(orderBy('sentAt', 'desc'));
        constraints.push(limit(limitCount));

        const q = query(collection(db, ALERTS_PATH), ...constraints);
        const snapshot = await getDocs(q);

        return snapshot.docs.map(d => ({
            ...d.data(),
            id: d.id,
        } as Alert));
    } catch (error) {
        console.error('Error fetching recent alerts:', error);
        return [];
    }
}

/**
 * Acknowledge an alert
 */
export async function acknowledgeAlert(
    alertId: string,
    adminEmail: string
): Promise<void> {
    try {
        await setDoc(
            doc(db, ALERTS_PATH, alertId),
            {
                acknowledged: true,
                acknowledgedBy: adminEmail,
                acknowledgedAt: Timestamp.now(),
            },
            { merge: true }
        );
    } catch (error) {
        console.error('Error acknowledging alert:', error);
        throw error;
    }
}

/**
 * Get alert statistics for a date range
 */
export async function getAlertStatistics(
    startDate: Date,
    endDate: Date
): Promise<AlertStats> {
    try {
        const q = query(
            collection(db, ALERTS_PATH),
            where('sentAt', '>=', Timestamp.fromDate(startDate)),
            where('sentAt', '<=', Timestamp.fromDate(endDate)),
            orderBy('sentAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const alerts = snapshot.docs.map(d => d.data() as Alert);

        const bySeverity: Record<string, number> = {};
        const byCategory: Record<string, number> = {};
        let acknowledged = 0;
        let totalResponseTimeMs = 0;
        let responsesCount = 0;

        alerts.forEach(alert => {
            bySeverity[alert.severity] = (bySeverity[alert.severity] || 0) + 1;

            alert.flagCategories.forEach(cat => {
                byCategory[cat] = (byCategory[cat] || 0) + 1;
            });

            if (alert.acknowledged) {
                acknowledged++;
                if (alert.acknowledgedAt && alert.sentAt) {
                    const sentMs = alert.sentAt instanceof Timestamp
                        ? alert.sentAt.toMillis()
                        : 0;
                    const ackMs = alert.acknowledgedAt instanceof Timestamp
                        ? alert.acknowledgedAt.toMillis()
                        : 0;
                    if (sentMs > 0 && ackMs > 0) {
                        totalResponseTimeMs += (ackMs - sentMs);
                        responsesCount++;
                    }
                }
            }
        });

        const avgResponseTimeMinutes = responsesCount > 0
            ? (totalResponseTimeMs / responsesCount) / 60000
            : 0;

        return {
            total: alerts.length,
            bySeverity,
            byCategory,
            acknowledged,
            avgResponseTimeMinutes,
        };
    } catch (error) {
        console.error('Error fetching alert statistics:', error);
        return {
            total: 0,
            bySeverity: {},
            byCategory: {},
            acknowledged: 0,
            avgResponseTimeMinutes: 0,
        };
    }
}

/**
 * Send a test alert to verify the pipeline works end-to-end
 */
export async function sendTestAlert(config: Partial<AlertConfig>): Promise<Alert> {
    const fullConfig: AlertConfig = { ...DEFAULT_ALERT_CONFIG, ...config };

    const testMessage: FlaggedMessage = {
        id: `test-${Date.now()}`,
        sessionID: 'test-session',
        appleID: 'test-apple-id',
        profileID: 'test-profile-id',
        explorerName: 'Test Explorer',
        helperType: 'general',
        origin: 'home',
        messageContent: 'This is a test alert message to verify the alerting pipeline.',
        flagCategories: ['self_harm'],
        maxSeverity: 5,
        flaggedAt: Timestamp.now(),
    };

    const alert = await createAlert(testMessage, fullConfig);
    await sendAlert(alert);
    return alert;
}
